import {
    useCreateRelationshipMutation,
    useDeleteRelationshipMutation,
    useDissolveGroupMutation,
    useUpdateGroupConfigMutation,
    useUpdateRelationshipMutation,
} from "@api/consolidation.api";

interface UseOwnershipMutationsParams {
    workspaceId: string;
    onRelationshipSaved: () => void;
    onRelationshipSaveError: (message: string) => void;
    onRelationshipDeleted: () => void;
    onDeleteSettled: () => void;
    onDissolveSettled: () => void;
}

/**
 * Bundles the consolidation mutations with the page's state side-effects
 * (closing panes/modals, toasts) so the page only wires UI.
 */
export function useOwnershipMutations({
    workspaceId,
    onRelationshipSaved,
    onRelationshipSaveError,
    onRelationshipDeleted,
    onDeleteSettled,
    onDissolveSettled,
}: UseOwnershipMutationsParams) {
    const createRelationshipMutation = useCreateRelationshipMutation(
        workspaceId,
        {
            onSuccess: onRelationshipSaved,
            onError: onRelationshipSaveError,
        },
    );
    const updateRelationshipMutation = useUpdateRelationshipMutation(
        workspaceId,
        {
            onSuccess: onRelationshipSaved,
            onError: onRelationshipSaveError,
        },
    );
    // Error toasts come from the global axios interceptor (which shows the
    // backend's message); callbacks only clean up local state.
    const deleteRelationshipMutation = useDeleteRelationshipMutation(
        workspaceId,
        {
            onSuccess: onRelationshipDeleted,
            onError: onDeleteSettled,
        },
    );
    const updateGroupConfigMutation = useUpdateGroupConfigMutation(workspaceId);
    const dissolveGroupMutation = useDissolveGroupMutation(workspaceId, {
        onSuccess: onDissolveSettled,
        onError: onDissolveSettled,
    });

    return {
        createRelationshipMutation,
        updateRelationshipMutation,
        deleteRelationshipMutation,
        updateGroupConfigMutation,
        dissolveGroupMutation,
    };
}
